import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { FormDomainScriptGuideService } from '@client/app/forms/domain-script-guide/form-domain-script-guide.service';
import { DOMAIN_SCRIPT_GUIDE_EVENTS } from '@client/app/forms/constants/domain-script-guide';

@Injectable({
    providedIn: 'root',
})
export class FormDomainScriptGuideStatisticsService {
    private showSetting = false;
    private cookielessTracking = false;
    private documentTracking = false;
    private documentClass = '';
    private documentExtensions: string[] = [];
    private documentIgnoreClass: string | string[] = [];

    settingChangeObs = new BehaviorSubject<null>(null);

    constructor (
        private activeFeatureService: ActiveFeatureService,
        private formDomainScriptGuideService: FormDomainScriptGuideService,
        private monEventService: MonEventService,
    ) {}

    getValue (): boolean {
        return this.hasAccessToFeature() && this.showSetting;
    }

    setValue (value: boolean, manualEdit = false): void {
        this.showSetting = value;
        this.updateSettings(manualEdit);
        this.settingChangeObs.next(null);
    }

    getCookielessTracking (): boolean {
        return this.getValue() && this.cookielessTracking;
    }

    setCookielessTracking (value: boolean, manualEdit = false): void {
        this.cookielessTracking = value;
        this.updateSettings(manualEdit);
        this.settingChangeObs.next(null);
    }

    getDocumentTracking (): boolean {
        return this.getValue() && this.documentTracking;
    }

    setDocumentTracking (value: boolean, manualEdit = false): void {
        this.documentTracking = value;
        this.updateSettings(manualEdit);
        this.settingChangeObs.next(null);
    }

    getFilledDocumentExtensions (): string[] {
        return this.getDocumentExtensions().filter((ext) => ext.length > 0);
    }

    getDocumentExtensions (): string[] {
        if (!Array.isArray(this.documentExtensions)) {
            return ['pdf', 'doc', 'ppt', 'docx', 'pptx'];
        }
        return this.documentExtensions;
    }

    setDocumentExtensions (value: string[], manualEdit = false): void {
        this.documentExtensions = value;
        this.updateSettings(manualEdit);
        this.settingChangeObs.next(null);
    }

    getDocumentClass (): string {
        if (typeof this.documentClass !== 'string') {
            return 'monsido_download';
        }
        return this.documentClass;
    }

    setDocumentClass (value: string, manualEdit = false): void {
        this.documentClass = value;
        this.updateSettings(manualEdit);
        this.settingChangeObs.next(null);
    }

    getDocumentIgnoreClass (): string | string[] {
        if (typeof this.documentIgnoreClass !== 'string') {
            return 'monsido_ignore_download';
        }
        return this.documentIgnoreClass;
    }

    setDocumentIgnoreClass (value: string, manualEdit = false): void {
        this.documentIgnoreClass = value;
        this.updateSettings(manualEdit);
        this.settingChangeObs.next(null);
    }

    hasAccessToFeature (): boolean {
        const domain = this.formDomainScriptGuideService.getDomain();
        return domain ? this.activeFeatureService.isFeatureActive('statistics', domain) : false;
    }

    private updateSettings (manualEdit = false): void {
        this.monEventService.run(DOMAIN_SCRIPT_GUIDE_EVENTS.FORM_UNSAVED_UPDATE);
        this.formDomainScriptGuideService.setScriptSettingBlock(
            'statistics',
            {
                enabled: this.getValue(),
                cookieless_tracking: this.getCookielessTracking(),
                document_tracking: {
                    enabled: this.getDocumentTracking(),
                    documentIgnoreCls: this.getDocumentIgnoreClass(),
                    document_cls: this.getDocumentClass(),
                    document_ext: this.getDocumentExtensions(),
                },
            },
            manualEdit,
        );
    }
}
